/**
 * Multi-country National ID validation
 * Supports:
 *   BE (Belgium): YY.MM.DD-XXX.CC - 11 digits, modulo 97 checksum
 *   TR (Turkey): TC Kimlik - 11 digits, modulo 10/11 algorithm
 *   Others: Free format, no strict validation
 */

export function stripDigits(value) {
  return (value || '').replace(/[\s.\-/]/g, '');
}

// === BELGIUM ===
function formatBE(digits) {
  if (digits.length !== 11) return digits;
  return `${digits.slice(0,2)}.${digits.slice(2,4)}.${digits.slice(4,6)}-${digits.slice(6,9)}.${digits.slice(9,11)}`;
}

function validateBE(value, birthDate) {
  const digits = stripDigits(value);
  if (!digits) return { valid: true };
  if (!/^\d+$/.test(digits)) return { valid: false, error: 'Sadece rakam giriniz' };
  if (digits.length !== 11) return { valid: false, error: `11 haneli olmalıdır (${digits.length} hane)` };

  const mm = parseInt(digits.slice(2, 4));
  const dd = parseInt(digits.slice(4, 6));
  const seq = parseInt(digits.slice(6, 9));
  const checkDigits = parseInt(digits.slice(9, 11));

  if (mm < 1 || mm > 12) return { valid: false, error: 'Geçersiz ay' };
  if (dd < 1 || dd > 31) return { valid: false, error: 'Geçersiz gün' };
  if (seq < 1 || seq > 997) return { valid: false, error: 'Geçersiz sıra no' };

  const base9 = parseInt(digits.slice(0, 9));
  const checkPre = 97 - (base9 % 97);
  const checkPost = 97 - ((2000000000 + base9) % 97);

  let century;
  if (checkDigits === checkPre) century = 1900;
  else if (checkDigits === checkPost) century = 2000;
  else return { valid: false, error: 'Kontrol basamağı hatalı (mod 97)' };

  const yy = parseInt(digits.slice(0, 2));
  const fullYear = century + yy;
  const gender = seq % 2 === 1 ? 'M' : 'F';

  if (birthDate) {
    const bd = new Date(birthDate);
    if (!isNaN(bd.getTime())) {
      if (bd.getFullYear() !== fullYear || (bd.getMonth() + 1) !== mm || bd.getDate() !== dd) {
        return { valid: true, warning: 'Doğum tarihi ile uyuşmuyor', gender, birthYear: fullYear };
      }
    }
  }
  return { valid: true, gender, birthYear: fullYear, info: `${gender === 'M' ? 'Erkek' : 'Kadın'} - ${fullYear}` };
}

// === TURKEY ===
function formatTR(digits) {
  return digits; // TC Kimlik has no special formatting
}

function validateTR(value) {
  const digits = stripDigits(value);
  if (!digits) return { valid: true };
  if (!/^\d+$/.test(digits)) return { valid: false, error: 'Sadece rakam giriniz' };
  if (digits.length !== 11) return { valid: false, error: `11 haneli olmalıdır (${digits.length} hane)` };
  if (digits[0] === '0') return { valid: false, error: 'İlk hane 0 olamaz' };

  const d = digits.split('').map(Number);

  // 10th digit check: ((d1+d3+d5+d7+d9)*7 - (d2+d4+d6+d8)) mod 10 == d10
  const oddSum = d[0] + d[2] + d[4] + d[6] + d[8];
  const evenSum = d[1] + d[3] + d[5] + d[7];
  const check10 = (oddSum * 7 - evenSum) % 10;
  if (check10 < 0 ? check10 + 10 : check10 !== d[9]) {
    return { valid: false, error: 'TC Kimlik kontrol hatası (10. hane)' };
  }

  // 11th digit check: (d1+d2+...+d10) mod 10 == d11
  const sumFirst10 = d.slice(0, 10).reduce((a, b) => a + b, 0);
  if (sumFirst10 % 10 !== d[10]) {
    return { valid: false, error: 'TC Kimlik kontrol hatası (11. hane)' };
  }

  return { valid: true, info: 'TC Kimlik No geçerli' };
}

// === MAIN EXPORTS ===

/**
 * Validate national ID based on country code
 * @param {string} value - The ID number
 * @param {string} countryCode - 'BE', 'TR', or any other
 * @param {string} [birthDate] - Optional YYYY-MM-DD for BE cross-validation
 * @returns {{ valid: boolean, error?: string, warning?: string, info?: string }}
 */
/**
 * Validate national ID - tries all known formats regardless of country code.
 * If it matches BE or TR format → valid. If it matches none → still valid (no error).
 */
export function validateNationalId(value, countryCode, birthDate) {
  const digits = stripDigits(value);
  if (!digits) return { valid: true };

  // Try Belgian validation
  const beResult = validateBE(value, birthDate);
  if (beResult.valid) return beResult;

  // Try Turkish validation
  const trResult = validateTR(value);
  if (trResult.valid) return trResult;

  // Matches none of the known formats → accept silently (no error)
  return { valid: true };
}

/**
 * Format national ID based on country code
 */
export function formatNationalId(digits, countryCode) {
  switch (countryCode) {
    case 'BE': return formatBE(digits);
    case 'TR': return formatTR(digits);
    default: return digits;
  }
}

/**
 * Get placeholder based on country code
 */
export function getPlaceholder(countryCode) {
  switch (countryCode) {
    case 'BE': return 'YY.MM.DD-XXX.CC';
    case 'TR': return 'TC Kimlik No (11 hane)';
    default: return 'Kimlik Numarası';
  }
}

/**
 * Get max length for input based on country
 */
export function getMaxDigits(countryCode) {
  switch (countryCode) {
    case 'BE': return 11;
    case 'TR': return 11;
    default: return 30; // generous for other countries
  }
}

// Keep old exports for backward compatibility with OCR modal
export const stripRRN = stripDigits;
export function formatRRN(digits) { return formatBE(digits); }
export function validateRRN(value, birthDate) { return validateBE(value, birthDate); }
export function getRRNErrorMessage(result) { return result?.valid ? null : (result?.error || null); }
export function getRRNWarningMessage(result) { return result?.warning || null; }
